import React from 'react';
import { ChevronDown, ChevronUp, Code, AlertCircle } from 'lucide-react';

interface DataPreparationStepProps {
  expandedSection: string | null;
  toggleSection: (section: string) => void;
  bill?: any;
  consumption?: any;
}

export const DataPreparationStep: React.FC<DataPreparationStepProps> = ({
  expandedSection,
  toggleSection,
  bill,
  consumption,
}) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 cursor-pointer" onClick={() => toggleSection('data-prep')}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-gray-100 text-gray-700 flex items-center justify-center text-sm font-semibold">1</div>
            <h3 className="text-base font-semibold text-black">Data Preparation & Alignment</h3>
          </div>
          {expandedSection === 'data-prep' ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
        </div>
      </div>
      {expandedSection === 'data-prep' && (
        <div className="p-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              The algorithm begins by aligning consumption and price data, handling different temporal resolutions
              and extending incomplete datasets.
            </p>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">DATA CHARACTERISTICS</p>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <p className="font-medium text-gray-900 mb-1">Consumption Data (Garibaldi)</p>
                  <ul className="space-y-1 text-gray-600">
                    <li>• Interval: 15-minute</li>
                    <li>• Records: 35,040 (full year)</li>
                    <li>• Total: 10,466,382 kWh</li>
                    <li>• Period: Apr 2024 - Mar 2025</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-900 mb-1">Price Data</p>
                  <ul className="space-y-1 text-gray-600">
                    <li>• Interval: Hourly</li>
                    <li>• Records: 4,705 (196 days)</li>
                    <li>• Period: Jan - Jul 2024</li>
                    <li>• Overlap: 105 days</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-4 rounded-lg">
              <p className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Code className="w-3 h-3" />
                ALIGNMENT ALGORITHM
              </p>
              <pre className="text-xs bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto font-mono">
{`# Step 1: Aggregate 15-min consumption to hourly
hourly_consumption = []
for hour in hours:
    # Sum four 15-min intervals
    hour_total = sum(consumption[hour*4:(hour+1)*4])
    hourly_consumption.append(hour_total)

# Step 2: Align with price data
aligned_data = []
for i, hour in enumerate(hourly_consumption):
    aligned_data.append({
        'consumption_kwh': hour,
        'price_usd_mwh': prices[i],
        'timestamp': timestamps[i]
    })

# Step 3: Handle missing data
if len(prices) < 8760:  # Less than full year
    # Extend using pattern analysis
    extended_prices = extend_with_pattern(prices)`}</pre>
            </div>

            <div className="bg-yellow-50 p-3 rounded-lg">
              <p className="text-xs font-semibold text-yellow-800 mb-1">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                Data Extension Methods
              </p>
              <ul className="text-xs text-yellow-700 space-y-1">
                <li><strong>Simple Repeat:</strong> Cycles through existing data to fill gaps</li>
                <li><strong>Weekly+Seasonal:</strong> Applies pattern-based adjustments (weekend factor: 0.85, summer: 1.20)</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};